import { useEffect, useState } from 'react';
import { FormattedMessage } from 'react-intl';
import { Spacer } from '@tink/core/src/components/atoms/Spacer/Spacer';
import { BackButton } from '../../../components/organisms/HeaderButton/HeaderButton';
import { LoadingSpinner } from '../../../components/atoms/LoadingSpinner/LoadingSpinner';
import { CredentialsEntity } from '../../../models/credentials';
import { PageView, UserInteractionEvent } from '../../../models/analytics';
import { PrimaryButton } from '../../../components/atoms/Button/Button';
import { usePostRedirectMessage } from '../../../components/screens/AwaitingCredentialsScreen/organisms/AwaitingThirdpartyAuthenticationContainer/parent-redirect-messenger';
import {
  UseThirdPartyRedirector,
  useThirdPartyRedirector as useThirdPartyRedirectorImport,
} from '../../../utilities/thirdparty-redirector/thirdparty-redirector';
import { messages } from '../../messages';
import { ContentBody } from '../../../components/atoms/ContentBody/ContentBody';
import { H2 } from '../../../components/atoms/Heading/Heading';
import { Text } from '../../../components/atoms/Text/Text';
import { BackButton as BackButtonType } from '../../../components/organisms/Header/HeaderMolecule';
import { usePageViewEvents } from '../../../core/Analytics/usePageViewEvents';
import { InteractionElement } from '../../../core/Analytics/ParentApplicationEventMessenger';
import {
  ContentContainer,
  Footer,
  Header,
  LayoutContainer,
  MainContent,
  TintedScreenWrapper,
} from './ThirdPartyRedirectPendingScreen.styles';

export type ThirdPartyRedirectPendingScreenProps = {
  providerName: string;
  credentials: CredentialsEntity;
  useThirdPartyRedirector?: UseThirdPartyRedirector;
} & BackButtonType;

export const ThirdPartyRedirectPendingScreen = ({
  providerName,
  onBack,
  credentials,
  useThirdPartyRedirector = useThirdPartyRedirectorImport,
}: ThirdPartyRedirectPendingScreenProps) => {
  const [isOpenProviderRedirectVisible, setIsOpenProviderRedirectVisible] = useState(false);
  const pageView = PageView.THIRD_PARTY_REDIRECT_PENDING_SCREEN;
  const { sendViewInteractionEvent } = usePageViewEvents({
    pageView,
  });
  const { thirdPartyRedirector } = useThirdPartyRedirector(credentials);
  usePostRedirectMessage(thirdPartyRedirector);

  const goBackWithAnalytics = onBack
    ? () => {
        sendViewInteractionEvent({
          interaction: UserInteractionEvent.BACK_BUTTON,
          element: InteractionElement.BACK_BUTTON,
        });
        onBack();
      }
    : undefined;

  useEffect(() => {
    const delay = setTimeout(() => {
      if (thirdPartyRedirector) {
        setIsOpenProviderRedirectVisible(true);
      }
    }, 2000);

    return () => clearTimeout(delay);
  }, [thirdPartyRedirector]);

  return (
    <TintedScreenWrapper data-testid="third-party-redirect-pending-screen">
      {goBackWithAnalytics && (
        <Header>
          <BackButton onBack={goBackWithAnalytics} />
        </Header>
      )}
      <ContentBody centerVertically>
        <LayoutContainer>
          <ContentContainer>
            <MainContent>
              <LoadingSpinner />
              <H2>
                <FormattedMessage {...messages.authorizeHeader} />
              </H2>
              <Text>
                <FormattedMessage {...messages.instructionsPayAuth} values={{ providerName }} />
              </Text>
            </MainContent>
            {isOpenProviderRedirectVisible && (
              <Footer>
                <Text color="secondary">
                  <FormattedMessage {...messages.providerInfo} values={{ providerName }} />
                </Text>
                <PrimaryButton
                  type="button"
                  pageView={PageView.THIRD_PARTY_REDIRECT_PENDING_SCREEN}
                  onClick={() => {
                    thirdPartyRedirector?.navigate();
                  }}
                >
                  <FormattedMessage {...messages.retry} />
                </PrimaryButton>
              </Footer>
            )}
          </ContentContainer>
        </LayoutContainer>
        <Spacer size="xlarge" />
      </ContentBody>
    </TintedScreenWrapper>
  );
};
